/**
 * CollectionSelectView - A searchable dropdown component for selecting items from a Collection
 *
 * This component uses a parent-child architecture to prevent input focus loss:
 * - CollectionSelectView (parent): Manages input and coordination
 * - CollectionDropdownView (child): Handles dropdown rendering only
 */

import { View } from '@core/View.js';
import MOJOUtils from '@core/utils/MOJOUtils.js';

/**
 * CollectionDropdownView - Child component for dropdown results only
 */
class CollectionDropdownView extends View {
  constructor(options = {}) {
    super({
      tagName: 'div',
      className: 'collection-dropdown-view dropdown-menu show w-100 position-absolute',
      style: 'max-height: 250px; overflow-y: auto; z-index: 1000;',
      template: `
        {{#data.loading}}
          <div class="dropdown-item text-center">
            <div class="spinner-border spinner-border-sm" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        {{/data.loading}}

        {{^data.loading}}
          {{#data.items}}
            <button type="button"
                    class="dropdown-item {{#isSelected}}active{{/isSelected}} {{#isFocused}}bg-light{{/isFocused}}"
                    data-action="select-item"
                    data-value="{{valueField}}"
                    data-label="{{labelField}}"
                    data-index="{{index}}">
              {{labelField}}
            </button>
          {{/data.items}}

          {{#data.showNoResults}}
            <div class="dropdown-item text-muted">
              {{#data.hasSearched}}No results found{{/data.hasSearched}}
              {{^data.hasSearched}}Start typing to search...{{/data.hasSearched}}
            </div>
          {{/data.showNoResults}}
        {{/data.loading}}
      `,
      ...options
    });

    this.collection = options.collection;
    this.labelField = options.labelField || 'name';
    this.valueField = options.valueField || 'id';
    this.selectedValue = options.selectedValue || '';
    this.loading = options.loading || false;
    this.hasSearched = options.hasSearched || false;
    this.focusedIndex = options.focusedIndex || -1;
  }

  async getViewData() {
    const items = this.collection ? this.collection.toJSON().map((item, index) => {
      const labelValue = MOJOUtils.getNestedValue(item, this.labelField);
      const fieldValue = MOJOUtils.getNestedValue(item, this.valueField);

      return {
        ...item,
        labelField: labelValue,
        valueField: fieldValue,
        isSelected: fieldValue == this.selectedValue,
        isFocused: index === this.focusedIndex,
        index
      };
    }) : [];

    return {
      loading: this.loading,
      hasSearched: this.hasSearched,
      showNoResults: !this.loading && this.hasSearched && items.length === 0,
      items
    };
  }

  async handleActionSelectItem(event, element) {
    event.preventDefault();
    const value = element.getAttribute('data-value');
    const label = element.getAttribute('data-label');
    this.emit('item-selected', { value, label });
  }

  updateState(state) {
    Object.assign(this, state);
  }

  updateFocusedItem(newIndex) {
    this.focusedIndex = newIndex;
    const items = this.element?.querySelectorAll('.dropdown-item[data-action="select-item"]');
    items?.forEach((item, index) => {
      item.classList.toggle('bg-light', index === this.focusedIndex);
    });
  }

  getItemCount() {
    return this.collection ? this.collection.length() : 0;
  }

  getFocusedItem() {
    if (this.focusedIndex >= 0 && this.collection) {
      const items = this.collection.toJSON();
      return items[this.focusedIndex] || null;
    }
    return null;
  }
}

/**
 * CollectionSelectView - Main component with stable input
 */
class CollectionSelectView extends View {
  constructor(options = {}) {
    super({
      className: 'collection-select-view',
      template: `
        <div class="position-relative">
          <input type="text"
                 class="form-control {{#data.hasError}}is-invalid{{/data.hasError}} {{#data.showClear}}pe-5{{/data.showClear}}"
                 placeholder="{{data.placeholder}}"
                 value="{{data.displayValue}}"
                 autocomplete="off" />

          <input type="hidden"
                 name="{{data.name}}"
                 value="{{data.selectedValue}}" />

          {{#data.showClear}}
            <button type="button"
                    class="btn btn-link position-absolute top-50 end-0 translate-middle-y me-2 p-0 border-0"
                    style="z-index: 10; color: #6c757d;"
                    data-action="clear-selection"
                    title="Clear selection">
              <i class="bi bi-x-circle"></i>
            </button>
          {{/data.showClear}}

          <div class="dropdown-container"></div>

          {{#data.hasError}}
            <div class="invalid-feedback">{{data.errorMessage}}</div>
          {{/data.hasError}}
        </div>
      `,
      ...options
    });

    // Configuration
    this.collection = options.collection;
    this.labelField = options.labelField || 'name';
    this.valueField = options.valueField || 'id';
    this.maxItems = options.maxItems || 10;
    this.placeholder = options.placeholder || 'Search...';
    this.debounceMs = options.debounceMs || 400;
    this.name = options.name || 'collection_select';
    this.emptyFetch = options.emptyFetch !== false;
    this.requiresActiveGroup = options.requiresActiveGroup || false;

    // State
    this.selectedValue = options.value || '0';
    this.selectedLabel = '';
    this.searchValue = '';
    this.showDropdown = false;
    this.loading = false;
    this.hasSearched = false;
    this.focusedIndex = -1;
    this.hasError = false;
    this.errorMessage = '';

    if (this.selectedValue && typeof this.selectedValue === 'object') {
      // Support dot notation in labelField and valueField
      this.selectedLabel = MOJOUtils.getNestedValue(this.selectedValue, this.labelField) || '';
      this.selectedValue = MOJOUtils.getNestedValue(this.selectedValue, this.valueField) || '0';
    }

    // Internal
    this.searchTimer = null;
    this.dropdownView = null;
    this.defaultParams = {};
    this.defaultParamsOption = options.defaultParams || null; // Can be dict or callback

    // Bind methods
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleInputEvents = this.handleInputEvents.bind(this);

  }

  onInit() {
    if (this.collection) {
      this.setupCollection();
    }
  }

  setupCollection() {
    this.defaultParams = { ...this.collection.params };
    this.collection.params.size = this.maxItems;
    this.defaultParams.size = this.maxItems;

    // Merge defaultParams from options (dict or callback)
    if (this.defaultParamsOption) {
      const extraParams = typeof this.defaultParamsOption === 'function' 
        ? this.defaultParamsOption() 
        : this.defaultParamsOption;
      
      if (extraParams && typeof extraParams === 'object') {
        Object.assign(this.defaultParams, extraParams);
        Object.assign(this.collection.params, extraParams);
      }
    }

    // Add active group filter if required
    if (this.requiresActiveGroup) {
      const app = this.getApp();
      if (app && app.activeGroup && app.activeGroup.id) {
        this.collection.params.group = app.activeGroup.id;
        this.defaultParams.group = app.activeGroup.id;
      }
    }

    this.collection.on('fetch:start', () => {
      this.loading = true;
      this.showDropdown = true;
      this.updateDropdown();
    });

    this.collection.on('fetch:end', () => {
      this.loading = false;
      this.showDropdown = true;
      this.updateDropdown();
    });

    if (this.selectedValue) {
      this.loadSelectedItem();
    }

    if (this.emptyFetch && this.collection.isEmpty()) {
      this.performInitialFetch();
    }
  }

  async performInitialFetch() {
    if (!this.collection) return;

    try {
      const fetchParams = { ...this.defaultParams };
      delete fetchParams.search;
      await this.collection.updateParams(fetchParams, true);
    } catch (error) {
      console.error('Initial fetch error:', error);
    }
  }

  async loadSelectedItem() {
    try {
      if (!this.selectedValue || this.selectedValue == '0') return;
      if (this.selectedLabel) return;
      const selectedModel = this.collection?.get(this.selectedValue);
      if (selectedModel) {
        // Support dot notation in labelField
        this.selectedLabel = this.getFieldValue(selectedModel, this.labelField);
        this.render(false);
        return;
      }

      let model = await this.collection.fetchOne(this.selectedValue);
      if (model) {
        // Support dot notation in labelField
        this.selectedLabel = this.getFieldValue(model, this.labelField) || `${model.constructor.name} #${model.id}`;
        this.render(false);
      }
    } catch (error) {
      console.error('Error loading selected item:', error);
    }
  }

  async getViewData() {
    let displayValue = '';
    if (this.showDropdown && this.hasSearched) {
      displayValue = this.searchValue;
    } else if (this.selectedValue && this.selectedLabel) {
      displayValue = this.selectedLabel;
    }

    return {
      name: this.name,
      placeholder: this.placeholder,
      displayValue: displayValue,
      selectedValue: this.selectedValue,
      showClear: !!(this.selectedValue && this.selectedValue !== '0' && this.selectedLabel),
      hasError: this.hasError,
      errorMessage: this.errorMessage
    };
  }

  async onAfterRender() {
    await super.onAfterRender();

    // Set up stable event listeners on input
    const input = this.getInput();
    if (input) {
      input.addEventListener('input', this.handleInputEvents);
      input.addEventListener('focus', this.handleInputEvents);
      input.addEventListener('keydown', this.handleKeyDown);
    }

    document.addEventListener('click', this.handleDocumentClick);

    // Create dropdown view
    this.createDropdownView();
  }

  async onBeforeDestroy() {
    await super.onBeforeDestroy();

    const input = this.getInput();
    if (input) {
      input.removeEventListener('input', this.handleInputEvents);
      input.removeEventListener('focus', this.handleInputEvents);
      input.removeEventListener('keydown', this.handleKeyDown);
    }

    document.removeEventListener('click', this.handleDocumentClick);

    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }

    if (this.dropdownView) {
      this.dropdownView.destroy();
    }
  }

  createDropdownView() {
    if (this.dropdownView) {
      this.dropdownView.destroy();
    }

    this.dropdownView = new CollectionDropdownView({
      collection: this.collection,
      labelField: this.labelField,
      valueField: this.valueField,
      selectedValue: this.selectedValue,
      loading: this.loading,
      hasSearched: this.hasSearched,
      focusedIndex: this.focusedIndex
    });

    this.dropdownView.on('item-selected', (data) => {
      this.selectItem(data.value, data.label);
    });
  }

  async handleInputEvents(event) {
    const input = event.target;

    if (event.type === 'focus') {
      this.showDropdown = true;
      if (!this.hasSearched && this.emptyFetch && this.collection?.isEmpty()) {
        this.performInitialFetch();
      }
      this.updateDropdown();
    } else if (event.type === 'input') {
      this.searchValue = input.value;
      this.showDropdown = true;
      this.hasSearched = true;
      this.focusedIndex = -1;

      if (this.searchValue !== this.selectedLabel) {
        this.selectedValue = '0';
        this.selectedLabel = '';
        this.emit('change', { value: '0', label: '' });
      }

      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }

      this.searchTimer = setTimeout(() => {
        this.performSearch();
      }, this.debounceMs);

      this.updateDropdown();
    }
  }

  async handleActionClearSelection(event, _element) {
    event.preventDefault();
    event.stopPropagation();

    this.clearSelection();
  }

  clearSelection() {
    this.selectedValue = '0';
    this.selectedLabel = '';
    this.searchValue = '';
    this.showDropdown = false;
    this.hasError = false;
    this.focusedIndex = -1;
    this.hasSearched = false;

    // Update input display
    const input = this.getInput();
    if (input) {
      input.value = '';
      input.focus(); // Focus back on input after clearing
    }

    // Update hidden input
    const hiddenInput = this.getHiddenInput();
    if (hiddenInput) {
      hiddenInput.value = '0';
    }

    this.updateDropdown();
    this.render(); // Re-render to hide clear button
    this.emit('change', { value: '0', label: '' });
  }

  async performSearch() {
    if (!this.collection) return;

    // this.loading = true;
    // this.updateDropdown();

    try {
      const searchParams = { ...this.defaultParams };
      if (this.searchValue && this.searchValue.trim()) {
        searchParams.search = this.searchValue.trim();
      }
      await this.collection.updateParams(searchParams, true);
    } catch (error) {
      console.error('Search error:', error);
      this.loading = false;
      this.updateDropdown();
    }
  }

  updateDropdown() {
    if (!this.dropdownView) return;

    this.dropdownView.updateState({
      selectedValue: this.selectedValue,
      loading: this.loading,
      hasSearched: this.hasSearched,
      focusedIndex: this.focusedIndex
    });

    if (this.showDropdown) {
      if (!this.dropdownView.isMounted()) {
        const container = this.element?.querySelector('.dropdown-container');
        if (container) {
          this.dropdownView.render(true, container);
        }
      } else {
        this.dropdownView.render();
      }
    } else if (this.dropdownView.isMounted()) {
      this.dropdownView.destroy();
      this.createDropdownView();
    }
  }

  selectItem(value, label) {
    this.selectedValue = value;
    this.selectedLabel = label;
    this.searchValue = '';
    this.showDropdown = false;
    this.hasError = false;
    this.focusedIndex = -1;
    this.hasSearched = false;

    // Update input display
    const input = this.getInput();
    if (input) {
      input.value = label;
    }

    // Update hidden input
    const hiddenInput = this.getHiddenInput();
    if (hiddenInput) {
      hiddenInput.value = value;
    }

    this.updateDropdown();
    this.emit('change', { value, label });
  }

  handleDocumentClick(event) {
    if (!this.element?.contains(event.target)) {
      this.showDropdown = false;
      this.focusedIndex = -1;
      this.updateDropdown();
    }
  }

  handleKeyDown(event) {
    if (!this.showDropdown || !this.collection) return;

    const itemCount = this.dropdownView?.getItemCount() || 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusedIndex = Math.min(this.focusedIndex + 1, itemCount - 1);
        this.dropdownView?.updateFocusedItem(this.focusedIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.focusedIndex = Math.max(this.focusedIndex - 1, 0);
        this.dropdownView?.updateFocusedItem(this.focusedIndex);
        break;

      case 'Enter': {
        event.preventDefault();
        const focusedItem = this.dropdownView?.getFocusedItem();
        if (focusedItem) {
          this.selectItem(focusedItem[this.valueField], focusedItem[this.labelField]);
        }
        break;
      }

      case 'Escape':
        event.preventDefault();
        this.showDropdown = false;
        this.focusedIndex = -1;
        this.updateDropdown();
        break;
    }
  }

  // Helper methods
  getInput() {
    return this.element?.querySelector('input[type="text"]');
  }

  getHiddenInput() {
    return this.element?.querySelector('input[type="hidden"]');
  }

  // Public API
  setValue(value, label = '') {
    this.selectedValue = value;
    this.selectedLabel = label;
    this.searchValue = '';
    this.hasError = false;
    this.hasSearched = false;

    const input = this.getInput();
    if (input) {
      input.value = label;
    }

    const hiddenInput = this.getHiddenInput();
    if (hiddenInput) {
      hiddenInput.value = value;
    }
  }

  getValue() {
    // Return null if value is 0 or '0' (no selection)
    if (this.selectedValue === 0 || this.selectedValue === '0') {
      return null;
    }
    return this.selectedValue;
  }

  getLabel() {
    return this.selectedLabel;
  }

  setError(message) {
    this.hasError = true;
    this.errorMessage = message;
    this.render();
  }

  clearError() {
    this.hasError = false;
    this.errorMessage = '';
    this.render();
  }

  focus() {
    const input = this.getInput();
    if (input) {
      input.focus();
    }
  }

  // FormBuilder integration
  getFormValue() {
    // Return null if value is 0 or '0' (no selection)
    if (this.selectedValue === 0 || this.selectedValue === '0') {
      return null;
    }
    return this.selectedValue;
  }

  setFormValue(value) {
    let newValue = value;
    let newLabel = '';

    if (newValue && typeof newValue === 'object') {
        // Support dot notation in labelField and valueField
        newLabel = MOJOUtils.getNestedValue(newValue, this.labelField) || '';
        newValue = MOJOUtils.getNestedValue(newValue, this.valueField);
    }

    newValue = newValue || '0';

    if (newValue == this.selectedValue) {
        return;
    }

    this.selectedValue = newValue;
    this.selectedLabel = newLabel;
    this.searchValue = '';
    this.hasSearched = false;
    this.showDropdown = false;
    this.hasError = false;

    if (this.selectedValue && this.selectedValue !== '0') {
        if (!this.selectedLabel) {
            this.selectedLabel = `${this.collection.getModelName()} #${this.selectedValue}`;
        }
        this.loadSelectedItem();
    } else {
        this.render();
    }
  }

  /**
   * Helper method to get field value from model or plain object with dot notation support
   * @param {Object|Model} item - The item to get value from
   * @param {string} fieldPath - The field path (supports dot notation)
   * @returns {*} The field value
   */
  getFieldValue(item, fieldPath) {
    if (!item || !fieldPath) return undefined;

    // If item has a get() method (Model instance), try using it first
    if (typeof item.get === 'function') {
      // Try to get the value using Model's get() method
      const value = item.get(fieldPath);
      // If get() returns undefined and path has dots, fall back to MOJOUtils
      if (value === undefined && fieldPath.includes('.')) {
        return MOJOUtils.getNestedValue(item, fieldPath);
      }
      return value;
    }

    // For plain objects, use MOJOUtils for dot notation support
    return MOJOUtils.getNestedValue(item, fieldPath);
  }

}

export default CollectionSelectView;
